import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

const ROW_TITLE_NAME = 'RowTitle';
const ROW_CONTENT_NAME = 'RowContent';

const RowTitle = ({ children }) => children;
RowTitle.displayName = ROW_TITLE_NAME;

const RowContent = ({ children }) => children;
RowContent.displayName = ROW_CONTENT_NAME;

/**
 * Component used to hide and show the content of other components.
 *
 * @param { bool } expanded  Used to define if the content will be expanded by default when it first apear
 *
 * @param { node } children Component to be used as title and the coponent that will be hide.
 *
 *
 *  How to use: see source code
 *
 * @description
 * @param { func } handleRowClick It is used to hide or show the content. It is injected in the component.
 *  To use it, must take it from the props and put in the `onClick` of the component.
 *
 * @param { bool } isExpanded It is used to notify the component if your content is hide or not. It is injected in the component.
 *  To use it, must take it from the props.
 *
 *  // Define the component where the children will be
 *  <ExpandRow>
 *
 *      // Inside must be defined the component to apear as the title of the row.
 *      <ExpandRow.RowTitle>
 *
 *          //  In that component will be injectted 'handleRowClick' and 'isExpanded' that can be accessed by the props.
 *          // { func } 'handleRowClick' is used to control when the component must show or hide the content.
 *          // { bool } 'isExpanded' is used for the component to know if it is hide or not. A use of that is to change the icon of the row.
 *          <YourComponentTitle />
 *
 *      </ExpandRow.RowTitle>
 *
 *      // Inside must be defined the component to apear as the content of the row.
 *      <ExpandRow.RowContent>
 *          <YourComponentContent />
 *      </ExpandRow.RowContent>
 *
 *    </ExpandRow>
 */

const ExpandRow = (props) => {
  const {
    children,
    expanded,
    onClick,
    noAutoExpand,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);

  const handleRowClick = useCallback(() => {
    if (onClick) {
      onClick();
    } else {
      setIsExpanded((previous) => !previous);
    }
  }, [onClick]);

  const rowTitle = !_.isEmpty(children) && children.find((child) => (child.type.displayName === ROW_TITLE_NAME));
  const rowContent = !_.isEmpty(children) && children.find((child) => (child.type.displayName === ROW_CONTENT_NAME));

  useEffect(() => {
    if (!noAutoExpand) {
      setIsExpanded(expanded);
    }
  }, [expanded, noAutoExpand]);

  return (
    <>
      {/**  Show a message at the console if a component is not used */}
      {!_.isEmpty(rowTitle) && !_.isEmpty(rowTitle.props.children) && typeof (rowTitle.props.children) === 'string'
      // eslint-disable-next-line no-console
      && console.log('Warning - \'ExpandRow.RowTitle\' only accepts components')}

      {/**  Row Title: the content that is visible and where can be clicked to show the hide content */}
      {!_.isEmpty(rowTitle) && !_.isEmpty(rowTitle.props.children) && typeof (rowTitle.props.children) !== 'string'
          && React.cloneElement(rowTitle.props.children, { handleRowClick, isExpanded })}

      {/**  Row Content that will be showed or hide */}
      {isExpanded && !_.isEmpty(rowContent) && !_.isEmpty(rowContent.props.children)
        && rowContent}

    </>
  );
};

ExpandRow.RowTitle = RowTitle;
ExpandRow.RowContent = RowContent;

ExpandRow.propTypes = {
  children: PropTypes.node.isRequired,
  expanded: PropTypes.bool,
  onClick: PropTypes.func,
  noAutoExpand: PropTypes.bool,
};

ExpandRow.defaultProps = {
  expanded: false,
  onClick: undefined,
  noAutoExpand: false,
};

export default ExpandRow;
